import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../utils/api';
import './Home.css';

const ROLE_NAMES = {
  villager: 'Dân Làng',
  werewolf: 'Ma Sói',
  seer: 'Tiên Tri',
  doctor: 'Bảo Vệ',
  witch: 'Phù Thuỷ',
  hunter: 'Thợ săn',
  lycan: 'Bán sói'
};

function Home() {
  const navigate = useNavigate();
  const [numPlayers, setNumPlayers] = useState(6);
  const [numWerewolves, setNumWerewolves] = useState(2);
  const [hasSeer, setHasSeer] = useState(true);
  const [hasDoctor, setHasDoctor] = useState(true);
  const [hasWitch, setHasWitch] = useState(true);
  const [hasHunter, setHasHunter] = useState(false);
  const [hasLycan, setHasLycan] = useState(false);
  const [allowWerewolfSkipKill, setAllowWerewolfSkipKill] = useState(false);
  const [loading, setLoading] = useState(false);

  const calculateMinWerewolves = () => {
    return Math.max(1, Math.floor(numPlayers / 4));
  };

  const calculateMaxWerewolves = () => {
    return Math.max(1, Math.floor(numPlayers / 3));
  };

  const handleCreateRoom = async () => {
    setLoading(true);
    try {
      const apiUrl = await getApiUrl();
      const response = await fetch(`${apiUrl}/api/room`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          config: {
            numPlayers,
            numWerewolves: Math.min(numWerewolves, calculateMaxWerewolves()),
            hasSeer,
            hasDoctor,
            hasWitch,
            hasHunter,
            hasLycan,
            allowWerewolfSkipKill
          }
        }),
      });

      const data = await response.json();
      if (data.roomId) {
        navigate(`/admin/${data.roomId}`);
      }
    } catch (error) {
      console.error('Error creating room:', error);
      alert('Lỗi khi tạo phòng. Vui lòng thử lại.');
    } finally {
      setLoading(false);
    }
  };

  const totalSpecialRoles = (hasSeer ? 1 : 0) + (hasDoctor ? 1 : 0) + (hasWitch ? 1 : 0) + (hasHunter ? 1 : 0) + (hasLycan ? 1 : 0);
  const totalRoles = numWerewolves + totalSpecialRoles;
  const remainingVillagers = numPlayers - totalRoles;

  return (
    <div className="home-container">
      <div className="container">
        <h1>🐺 Trò Chơi Ma Sói 🐺</h1>
        <p style={{ textAlign: 'center', color: '#666', marginBottom: '30px' }}>
          Tạo phòng chơi mới và mời bạn bè tham gia
        </p>

        <div className="config-section">
          <h2>Cấu Hình Phòng Chơi</h2>

          <div className="form-group">
            <label className="label">Số Lượng Người Chơi:</label>
            <input
              type="number"
              className="input"
              min="4"
              max="20"
              value={numPlayers}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 4;
                setNumPlayers(val);
                const maxWolves = calculateMaxWerewolves();
                if (numWerewolves > maxWolves) {
                  setNumWerewolves(maxWolves);
                }
              }}
            />
          </div>

          <div className="form-group">
            <label className="label">Số Lượng Ma Sói:</label>
            <input
              type="number"
              className="input"
              min={calculateMinWerewolves()}
              max={calculateMaxWerewolves()}
              value={numWerewolves}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 1;
                const max = calculateMaxWerewolves();
                const min = calculateMinWerewolves();
                setNumWerewolves(Math.max(min, Math.min(val, max)));
              }}
            />
            <small style={{ color: '#666' }}>
              Gợi ý: {calculateMinWerewolves()} - {calculateMaxWerewolves()} sói
            </small>
          </div>

          <div className="roles-section">
            <h3>Vai Trò Đặc Biệt:</h3>
            
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={hasSeer}
                  onChange={(e) => setHasSeer(e.target.checked)}
                />
                <strong>{ROLE_NAMES.seer}</strong> - Có thể soi xem ai là sói mỗi đêm
              </label>
            </div>

            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={hasDoctor}
                  onChange={(e) => setHasDoctor(e.target.checked)}
                />
                <strong>{ROLE_NAMES.doctor}</strong> - Có thể bảo vệ ai đó khỏi sói mỗi đêm
              </label>
            </div>

            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={hasWitch}
                  onChange={(e) => setHasWitch(e.target.checked)}
                />
                <strong>{ROLE_NAMES.witch}</strong> - Có thể cứu hoặc giết ai đó (mỗi kỹ năng dùng 1 lần)
              </label>
            </div>

            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={hasHunter}
                  onChange={(e) => setHasHunter(e.target.checked)}
                />
                <strong>{ROLE_NAMES.hunter}</strong> - Có thể bắn một người khi chết
              </label>
            </div>

            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={hasLycan}
                  onChange={(e) => setHasLycan(e.target.checked)}
                />
                <strong>{ROLE_NAMES.lycan}</strong> - Biến thành sói nếu bị sói tấn công
              </label>
            </div>
          </div>

          <div className="roles-section" style={{ marginTop: '20px' }}>
            <h3>Tùy Chọn:</h3>
            
            <div className="checkbox-group">
              <label>
                <input
                  type="checkbox"
                  className="checkbox"
                  checked={allowWerewolfSkipKill}
                  onChange={(e) => setAllowWerewolfSkipKill(e.target.checked)}
                />
                <strong>Cho phép sói không giết ai</strong> - Sói có thể bỏ phiếu "Không giết" trong đêm
              </label>
            </div>
          </div>

          <div className="summary">
            <h3>Tóm Tắt:</h3>
            <ul>
              <li>Ma Sói: {numWerewolves}</li>
              {hasSeer && <li>Tiên Tri: 1</li>}
              {hasDoctor && <li>Bảo Vệ: 1</li>}
              {hasWitch && <li>Phù Thuỷ: 1</li>}
              {hasHunter && <li>Thợ săn: 1</li>}
              {hasLycan && <li>Bán sói: 1</li>}
              <li>Dân Làng: {remainingVillagers}</li>
            </ul>
            {remainingVillagers < 0 && (
              <p style={{ color: '#e74c3c', marginTop: '10px' }}>
                ⚠️ Cảnh báo: Số vai trò vượt quá số người chơi!
              </p>
            )}
          </div>

          <button
            className="button"
            onClick={handleCreateRoom}
            disabled={loading || remainingVillagers < 0}
            style={{ width: '100%', marginTop: '20px' }}
          >
            {loading ? 'Đang tạo phòng...' : 'Tạo Phòng Chơi'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default Home;


